import { useEffect, useState, createContext, useContext, useReducer, useRef } from 'react';
import { DataPassingDispatchActionType } from 'automation-ui-formula-editor/data-passing-panel/constants/DataPassingActionConstants';
import FetchObjectPanel from 'automation-ui-formula-editor/fetch-object-panel/FetchObjectPanel';
import { isClickEventFromAddDataSourcePanel, isClickEventFromDataPanel } from 'automation-ui-formula-editor/data-passing-panel/utils/isClickEventFrom';
import { DataPanelModes } from 'automation-ui-formula-editor/data-passing-panel/types/DataPassingPanelTypes';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const DataPanelVisibleStates = {
  OPEN: 'OPEN',
  COLLAPSED: 'COLLAPSED',
  CLOSED: 'CLOSED'
};
export const DataPassingContext = /*#__PURE__*/createContext(undefined);
function dataPassingReducer(state, action) {
  switch (action.type) {
    case DataPassingDispatchActionType.HYDRATE:
      {
        const {
          dataPanelMode = DataPanelModes.PROPERTY_MODE,
          dataSourceAllowList,
          propertyFieldTypeAllowList,
          propertyTypeAllowList,
          requiredPermissionLevel,
          hideNestedDataSources,
          onDataCoordinateSelected,
          onDataPropertySelected,
          targetInputRef
        } = action.payload;
        return Object.assign({}, state, {
          dataPanelMode,
          dataSourceAllowList,
          propertyFieldTypeAllowList,
          propertyTypeAllowList,
          requiredPermissionLevel,
          hideNestedDataSources,
          onDataCoordinateSelected,
          onDataPropertySelected,
          targetInputRef
        });
      }
    case DataPassingDispatchActionType.DEHYDRATE:
      {
        return Object.assign({}, state, {
          dataSourceAllowList: undefined,
          propertyFieldTypeAllowList: undefined,
          propertyTypeAllowList: undefined,
          requiredPermissionLevel: undefined,
          onDataCoordinateSelected: undefined,
          onDataPropertySelected: undefined,
          targetInputRef: undefined
        });
      }
    case DataPassingDispatchActionType.PANEL_DISPLAY_STATUS_CHANGE:
      {
        return Object.assign({}, state, {
          actionConfigDataPanelDisplay: action.payload
        });
      }
    case DataPassingDispatchActionType.HYDRATE_FROM_PANEL:
      {
        const {
          dataPanelRef,
          fetchObjectCreatedCallback,
          fetchObjectDeletedCallback,
          searchForDataTokenCallback
        } = action.payload;
        return Object.assign({}, state, {
          dataPanelRef,
          fetchObjectCreatedCallback,
          fetchObjectDeletedCallback,
          searchForDataTokenCallback
        });
      }
    case DataPassingDispatchActionType.DEHYDRATE_FROM_PANEL:
      {
        return Object.assign({}, state, {
          fetchObjectCreatedCallback: undefined,
          fetchObjectDeletedCallback: undefined,
          onDataCoordinateSelected: undefined,
          onDataPropertySelected: undefined,
          searchForDataTokenCallback: undefined,
          targetInputRef: undefined
        });
      }
    case DataPassingDispatchActionType.ADDED_DATA_SOURCE:
      return Object.assign({}, state, {
        lastAddedEditedDataSource: action.payload.prefix
      });
    default:
      {
        throw new Error(`Unhandled action: ${action}`);
      }
  }
}
function DataPassingProvider({
  actionConfigDataPanelDisplay,
  children,
  disableAddDataSource,
  flowId,
  flowObjectTypeId,
  initialState: initialStateProp,
  isEmbedded,
  isFlowEnabled,
  onChangeActionConfigDataPanelDisplay,
  dataSourceBlockList
}) {
  const refScrollDataPanelDataTokenStore = useState({});
  const [isAddDataSourcePanelVisible, setIsAddDataSourcePanelVisible] = useState(false);
  const [fetchObjectPanelOptions, setFetchObjectPanelOptions] = useState();
  const handleOpenFetchObjectPanel = options => {
    setIsAddDataSourcePanelVisible(true);
    setFetchObjectPanelOptions(options);
  };
  const initialState = Object.assign({
    actionConfigDataPanelDisplay,
    actionId: null,
    canvasScrollElementRef: undefined,
    dataPanelMode: DataPanelModes.PROPERTY_MODE,
    dataPanelRef: undefined,
    disableAddDataSource,
    fetchObjectCreatedCallback: undefined,
    fetchObjectDeletedCallback: undefined,
    flowId,
    flowObjectTypeId,
    hideNestedDataSources: true,
    isEmbedded,
    lastAddedEditedDataSource: null,
    onChangeActionConfigDataPanelDisplay,
    onDataCoordinateSelected: undefined,
    onDataPropertySelected: undefined,
    openFetchObjectPanel: handleOpenFetchObjectPanel,
    previousActionId: null,
    searchForDataTokenCallback: undefined,
    targetInputRef: undefined,
    dataSourceBlockList
  }, initialStateProp);
  const [state, dispatch] = useReducer(dataPassingReducer, initialState);
  const value = {
    state,
    dispatch,
    refScrollDataPanelDataTokenStore,
    isAddDataSourcePanelVisible
  };
  const clickHandlerRef = useRef(null);
  const handleMouseUpEvent = event => {
    // Determine if click is in the component that is currently bound to the panel
    // If not, dehyrdate the panel so the user can no longer insert into the inactive input
    //
    // Dev note: Cover the case where a click finishes outside the target, but the input still has focus. For example highlight text, but mouse up outside of the text area
    if (isClickEventFromAddDataSourcePanel(event) || isClickEventFromDataPanel(event) || state.targetInputRef.current.contains(document.activeElement) || state.targetInputRef.current.contains(event.target) || event.target.closest('[data-panel-blur-ignore="true"]')) {
      return;
    }
    if (clickHandlerRef.current) {
      document.removeEventListener('mouseup', clickHandlerRef.current);
    }
    clickHandlerRef.current = null;
    dispatch({
      type: DataPassingDispatchActionType.DEHYDRATE,
      payload: {}
    });
    onChangeActionConfigDataPanelDisplay(DataPanelVisibleStates.CLOSED);
  };

  // changes from the provider props need to be dispatched into the context
  // state. Dispatch changes of props so downline can pick them up in the state
  useEffect(() => {
    dispatch({
      type: DataPassingDispatchActionType.PANEL_DISPLAY_STATUS_CHANGE,
      payload: actionConfigDataPanelDisplay
    });
  }, [actionConfigDataPanelDisplay]);

  // PM: The data panel should not be open in a dehydrated state. When we dehydrate
  // call onChangeActionConfigDataPanelDisplay to close out the panel fully in the action config
  useEffect(() => {
    if (state.targetInputRef) {
      return;
    }
    onChangeActionConfigDataPanelDisplay(DataPanelVisibleStates.CLOSED);
    // PM: We dont need onChangeActionConfigDataPanelDisplay to fire this use effect so its omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.targetInputRef]);
  useEffect(() => {
    clickHandlerRef.current = handleMouseUpEvent;
    return function () {
      if (clickHandlerRef.current) {
        document.removeEventListener('mouseup', clickHandlerRef.current);
        clickHandlerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // // Force the data panel to dehydrate from the bound target input if the user
  // // clicks outside of the data panel or the the add data source panel
  useEffect(() => {
    if (!state.targetInputRef || !state.targetInputRef.current) {
      return;
    }
    if (clickHandlerRef.current) {
      document.removeEventListener('mouseup', clickHandlerRef.current);
    }
    clickHandlerRef.current = handleMouseUpEvent;
    document.addEventListener('mouseup', clickHandlerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.targetInputRef]);
  const handleCloseFetchObjectPanel = () => {
    setIsAddDataSourcePanelVisible(false);
    setFetchObjectPanelOptions(undefined);
  };
  return /*#__PURE__*/_jsx(DataPassingContext.Provider, {
    value: value,
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [isAddDataSourcePanelVisible && /*#__PURE__*/_jsx(FetchObjectPanel, {
        flowId: flowId,
        fetchObjectCreatedCallback: prefix => {
          var _state$fetchObjectCre;
          dispatch({
            type: DataPassingDispatchActionType.ADDED_DATA_SOURCE,
            payload: {
              prefix
            }
          });
          (_state$fetchObjectCre = state.fetchObjectCreatedCallback) === null || _state$fetchObjectCre === void 0 || _state$fetchObjectCre.call(state, prefix);
        },
        fetchObjectDeletedCallback: state.fetchObjectDeletedCallback,
        flowObjectTypeId: state.flowObjectTypeId,
        initialDisplayMode: fetchObjectPanelOptions === null || fetchObjectPanelOptions === void 0 ? void 0 : fetchObjectPanelOptions.initialDisplayMode,
        objectTypeFilter: fetchObjectPanelOptions === null || fetchObjectPanelOptions === void 0 ? void 0 : fetchObjectPanelOptions.objectTypeFilter,
        isFlowEnabled: isFlowEnabled,
        onClose: handleCloseFetchObjectPanel
      }), children]
    })
  });
}
function useDataPassingContext() {
  const context = useContext(DataPassingContext);
  if (context === undefined) {
    throw new Error('useDataPassingContext must be used within a DataPassingProvider');
  }
  return context;
}
export { DataPassingProvider, useDataPassingContext };